import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Plus, Edit, Trash2, Save, X, Image as ImageIcon } from "lucide-react";
import { useAuth } from "../../utils/auth";
import { isAdmin } from "../../utils/roles";
import { fetchTypes } from "../../api/TypeAPIs";
import {
  fetchProductById,
  createProduct,
  updateProduct,
  deleteProduct,
  fetchCategoryTypes,
  fetchColors,
  fetchCatalogCategories,
  fetchProductVariants,
  createProductVariant,
  updateProductVariant,
  deleteProductVariant,
} from "../../api/CatalogAPIs";
import {
  CatalogShell,
  ConfirmDialog,
  StockBadge,
  TableSkeleton,
  EmptyState,
  Switch,
  parseBilingual,
  joinBilingual,
  displayAr,
  resolveMediaUrl,
  compressImageFile,
  splitVariantImages,
  joinVariantImages,
  isCatalogMetaUrl,
  DEFAULT_LOW_STOCK,
} from "./catalogUi";

const emptyProduct = {
  nameAr: "",
  nameEn: "",
  descriptionAr: "",
  descriptionEn: "",
  categoryTypeId: "",
  categoryId: "",
  allowedColorIds: [],
  basePrice: "",
  requiresVariants: true,
  stockQuantity: "0",
  allowsPersonalization: false,
  requiresArabicName: false,
  requiresEnglishName: false,
  maxNameLength: "12",
  requiresDeposit: false,
  isActive: true,
  imageUrls: [],
  personalizationLabelAr: "",
  personalizationLabelEn: "",
};

const emptyVariant = {
  nameAr: "",
  nameEn: "",
  code: "",
  price: "",
  stockQuantity: "0",
  lowStockThreshold: String(DEFAULT_LOW_STOCK),
  colorId: "",
  typeId: "",
  isAvailable: true,
  imageUrls: [],
};

const ImagePicker = ({ urls, onChange }) => {
  const handleFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    try {
      const encoded = [];
      for (const file of files) {
        encoded.push(await compressImageFile(file));
      }
      onChange([...(urls || []), ...encoded]);
    } catch {
      toast.error("تعذر رفع الصورة");
    }
    event.target.value = "";
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-3">
        {(urls || []).filter((url) => !isCatalogMetaUrl(url)).map((url, index) => (
          <div key={`${url}-${index}`} className="relative w-24 h-24 rounded-xl overflow-hidden border">
            <img src={resolveMediaUrl(url)} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => onChange(urls.filter((_, i) => i !== index))}
              className="absolute top-1 left-1 bg-black/60 text-white rounded-full p-1"
            >
              <X size={12} />
            </button>
          </div>
        ))}
      </div>
      <label className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 cursor-pointer">
        <ImageIcon size={16} /> إضافة صور
        <input type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} />
      </label>
    </div>
  );
};

const ProductFormPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { authState } = useAuth();
  const canManage = isAdmin(authState.role);

  const [form, setForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  const [variantForm, setVariantForm] = useState(null);
  const [editingVariant, setEditingVariant] = useState(null);
  const [savingVariant, setSavingVariant] = useState(false);
  const [deleteVariant, setDeleteVariant] = useState(null);
  const [deletingVariant, setDeletingVariant] = useState(false);
  const [deleteProductOpen, setDeleteProductOpen] = useState(false);
  const [deletingProduct, setDeletingProduct] = useState(false);

  const { data: product, isLoading: loadingProduct } = useQuery({
    queryKey: ["catalogProduct", id],
    queryFn: () => fetchProductById(id),
    enabled: canManage && isEdit,
  });

  const { data: categoryTypes = [] } = useQuery({
    queryKey: ["catalogCategoryTypes"],
    queryFn: fetchCategoryTypes,
    enabled: canManage,
  });

  const { data: colors = [] } = useQuery({
    queryKey: ["catalogColors"],
    queryFn: fetchColors,
    enabled: canManage,
  });

  const { data: types = [] } = useQuery({
    queryKey: ["types"],
    queryFn: fetchTypes,
    enabled: canManage,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ["catalogCategories", form.categoryTypeId],
    queryFn: () => fetchCatalogCategories(form.categoryTypeId || undefined),
    enabled: canManage,
  });

  const { data: variants = [], isLoading: loadingVariants, refetch: refetchVariants } = useQuery({
    queryKey: ["catalogProductVariants", id],
    queryFn: () => fetchProductVariants(id),
    enabled: canManage && isEdit,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (!authState.isValidating && !canManage) navigate("/login");
  }, [authState, canManage, navigate]);

  useEffect(() => {
    if (!product) return;
    const names = parseBilingual(product.name);
    const descriptions = parseBilingual(product.description);
    const { urls, meta } = splitVariantImages(
      product.imageUrls?.length ? product.imageUrls : product.imageUrl ? [product.imageUrl] : []
    );
    setForm({
      nameAr: names.ar,
      nameEn: names.en,
      descriptionAr: descriptions.ar,
      descriptionEn: descriptions.en,
      categoryTypeId: product.categoryTypeId ? String(product.categoryTypeId) : "",
      categoryId: product.categoryId ? String(product.categoryId) : "",
      allowedColorIds: product.allowedColorIds || [],
      basePrice: product.basePrice != null ? String(product.basePrice) : "",
      requiresVariants: product.requiresVariants !== false,
      stockQuantity: String(product.stockQuantity ?? 0),
      allowsPersonalization: !!product.allowsPersonalization,
      requiresArabicName: !!product.requiresArabicName,
      requiresEnglishName: !!product.requiresEnglishName,
      maxNameLength: String(product.maxNameLength || 12),
      requiresDeposit: !!product.requiresDeposit,
      isActive: product.isActive !== false,
      imageUrls: urls,
      personalizationLabelAr: meta.personalizationLabelAr || "",
      personalizationLabelEn: meta.personalizationLabelEn || "",
    });
  }, [product]);

  const categoryOptions = useMemo(() => {
    if (!form.categoryTypeId) return categories;
    return categories.filter(
      (category) => !category.categoryTypeId || String(category.categoryTypeId) === String(form.categoryTypeId)
    );
  }, [categories, form.categoryTypeId]);

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    const name = joinBilingual(form.nameAr, form.nameEn);
    if (!name) {
      toast.error("اسم المنتج مطلوب");
      return;
    }
    if (!form.categoryTypeId) {
      toast.error("نوع الفئة مطلوب");
      return;
    }
    if (!form.requiresVariants && (!form.basePrice || Number(form.basePrice) <= 0)) {
      toast.error("السعر الأساسي مطلوب للمنتجات بدون متغيرات");
      return;
    }

    const imageUrls = joinVariantImages(form.imageUrls, {
      personalizationLabelAr: form.personalizationLabelAr,
      personalizationLabelEn: form.personalizationLabelEn,
    });
    const payload = {
      categoryTypeId: Number(form.categoryTypeId),
      categoryId: form.categoryId ? Number(form.categoryId) : null,
      name,
      description: joinBilingual(form.descriptionAr, form.descriptionEn) || null,
      imageUrl: form.imageUrls[0] || null,
      imageUrls,
      basePrice: form.basePrice ? Number(form.basePrice) : null,
      allowedColorIds: form.allowedColorIds,
      requiresVariants: form.requiresVariants,
      stockQuantity: form.requiresVariants ? 0 : Math.max(0, parseInt(form.stockQuantity, 10) || 0),
      isActive: form.isActive,
      allowsPersonalization: form.allowsPersonalization,
      requiresArabicName: form.requiresArabicName,
      requiresEnglishName: form.requiresEnglishName,
      maxNameLength: parseInt(form.maxNameLength, 10) || 12,
      requiresDeposit: form.requiresDeposit,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await updateProduct(id, payload);
        toast.success("تم حفظ المنتج");
        queryClient.invalidateQueries({ queryKey: ["catalogProduct", id] });
        queryClient.invalidateQueries({ queryKey: ["catalogProductsFilter"] });
        queryClient.invalidateQueries({ queryKey: ["catalogProductsAll"] });
      } else {
        const created = await createProduct(payload);
        toast.success("تم إنشاء المنتج");
        navigate(`/admin/catalog/products/${created.productId}`);
      }
    } catch (err) {
      toast.error(err.message || "فشل حفظ المنتج");
    } finally {
      setSaving(false);
    }
  };

  const openVariantCreate = () => {
    setEditingVariant(null);
    setVariantForm({
      ...emptyVariant,
      colorId: form.allowedColorIds[0] ? String(form.allowedColorIds[0]) : "",
    });
  };

  const openVariantEdit = (variant) => {
    const { urls, meta } = splitVariantImages(variant.imageUrls);
    setEditingVariant(variant);
    setVariantForm({
      nameAr: meta.nameAr,
      nameEn: meta.nameEn,
      code: variant.code || "",
      price: String(variant.price ?? ""),
      stockQuantity: String(variant.stockQuantity ?? 0),
      lowStockThreshold: String(meta.lowStockThreshold || DEFAULT_LOW_STOCK),
      colorId: variant.colorId ? String(variant.colorId) : "",
      typeId: variant.typeId ? String(variant.typeId) : "",
      isAvailable: variant.isAvailable !== false,
      imageUrls: urls,
    });
  };

  const saveVariant = async (e) => {
    e.preventDefault();
    if (!variantForm.colorId) {
      toast.error("اللون مطلوب");
      return;
    }
    if (!variantForm.code.trim()) {
      toast.error("SKU مطلوب");
      return;
    }
    const price = Number(variantForm.price);
    const stock = parseInt(variantForm.stockQuantity, 10);
    const threshold = parseInt(variantForm.lowStockThreshold, 10);
    if (!Number.isFinite(price) || price <= 0) {
      toast.error("السعر يجب أن يكون رقماً أكبر من صفر");
      return;
    }
    if (!Number.isFinite(stock) || stock < 0) {
      toast.error("المخزون يجب أن يكون رقماً صحيحاً");
      return;
    }
    if (!Number.isFinite(threshold) || threshold < 0) {
      toast.error("حد المخزون المنخفض غير صالح");
      return;
    }

    const payload = {
      productId: Number(id),
      typeId: variantForm.typeId ? Number(variantForm.typeId) : null,
      colorId: Number(variantForm.colorId),
      stockQuantity: stock,
      price,
      code: variantForm.code.trim(),
      imageUrls: joinVariantImages(variantForm.imageUrls, {
        nameAr: variantForm.nameAr,
        nameEn: variantForm.nameEn,
        lowStockThreshold: threshold,
      }),
      isAvailable: variantForm.isAvailable,
    };

    setSavingVariant(true);
    try {
      if (editingVariant) {
        await updateProductVariant(editingVariant.productVariantId, payload);
        toast.success("تم تحديث المتغير");
      } else {
        await createProductVariant(payload);
        toast.success("تم إضافة المتغير");
      }
      setVariantForm(null);
      setEditingVariant(null);
      refetchVariants();
    } catch (err) {
      toast.error(err.message || "فشل حفظ المتغير");
    } finally {
      setSavingVariant(false);
    }
  };

  const confirmDeleteVariant = async () => {
    if (!deleteVariant) return;
    setDeletingVariant(true);
    try {
      await deleteProductVariant(deleteVariant.productVariantId);
      toast.success("تم حذف المتغير");
      setDeleteVariant(null);
      refetchVariants();
    } catch (err) {
      toast.error(err.message || "تعذر حذف المتغير");
    } finally {
      setDeletingVariant(false);
    }
  };

  const confirmDeleteProduct = async () => {
    if (!id) return;
    setDeletingProduct(true);
    try {
      await deleteProduct(id);
      toast.success("تم حذف المنتج");
      navigate("/admin/catalog/products");
    } catch (err) {
      toast.error(err.message || "تعذر حذف المنتج");
    } finally {
      setDeletingProduct(false);
      setDeleteProductOpen(false);
    }
  };

  const toggleColor = (colorId) => {
    setForm((prev) => {
      const exists = prev.allowedColorIds.includes(colorId);
      return {
        ...prev,
        allowedColorIds: exists
          ? prev.allowedColorIds.filter((idValue) => idValue !== colorId)
          : [...prev.allowedColorIds, colorId],
      };
    });
  };

  if (!canManage) return null;

  if (isEdit && loadingProduct) {
    return (
      <CatalogShell title="تعديل المنتج">
        <div className="bg-white rounded-2xl p-8 animate-pulse h-96" />
      </CatalogShell>
    );
  }

  return (
    <CatalogShell
      title={isEdit ? "تعديل المنتج" : "إضافة منتج"}
      subtitle={isEdit ? "البيانات الأساسية والمتغيرات" : "احفظ المنتج أولاً ثم أضف المتغيرات"}
    >
      <ToastContainer position="top-center" />

      <form onSubmit={handleSaveProduct} className="bg-white/90 rounded-2xl shadow border border-white/30 p-5 md:p-8 space-y-8">
        <section>
          <h2 className="text-xl font-bold mb-4">بيانات أساسية</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-600">الاسم عربي</label>
              <input
                value={form.nameAr}
                onChange={(e) => setForm({ ...form, nameAr: e.target.value })}
                className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl"
              />
            </div>
            <div>
              <label className="text-sm text-gray-600">الاسم إنجليزي</label>
              <input
                value={form.nameEn}
                onChange={(e) => setForm({ ...form, nameEn: e.target.value })}
                className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl"
                dir="ltr"
              />
            </div>
            <div>
              <label className="text-sm text-gray-600">الوصف عربي</label>
              <textarea
                value={form.descriptionAr}
                onChange={(e) => setForm({ ...form, descriptionAr: e.target.value })}
                className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl min-h-[90px]"
              />
            </div>
            <div>
              <label className="text-sm text-gray-600">الوصف إنجليزي</label>
              <textarea
                value={form.descriptionEn}
                onChange={(e) => setForm({ ...form, descriptionEn: e.target.value })}
                className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl min-h-[90px]"
                dir="ltr"
              />
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-4">التصنيف</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-600">نوع الفئة</label>
              <select
                value={form.categoryTypeId}
                onChange={(e) => setForm({ ...form, categoryTypeId: e.target.value, categoryId: "" })}
                className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl bg-white"
                required
              >
                <option value="">اختر نوع الفئة</option>
                {categoryTypes.map((type) => (
                  <option key={type.categoryTypeId} value={type.categoryTypeId}>
                    {displayAr(type.name)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-600">الفئة الفرعية (اختياري)</label>
              <select
                value={form.categoryId}
                onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
                className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl bg-white"
              >
                <option value="">بدون</option>
                {categoryOptions.map((category) => (
                  <option key={category.categoryId} value={category.categoryId}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-4">
            <label className="text-sm text-gray-600">اللون</label>
            <select
              value=""
              onChange={(e) => {
                if (e.target.value) toggleColor(Number(e.target.value));
              }}
              className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl bg-white"
            >
              <option value="">أضف لوناً من الكتالوج</option>
              {colors.map((color) => (
                <option key={color.colorId} value={color.colorId}>
                  {displayAr(color.name)} {color.hexCode ? `(${color.hexCode})` : ""}
                </option>
              ))}
            </select>
            <div className="flex flex-wrap gap-2 mt-3">
              {colors.map((color) => {
                const selected = form.allowedColorIds.includes(color.colorId);
                return (
                  <button
                    type="button"
                    key={color.colorId}
                    onClick={() => toggleColor(color.colorId)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${
                      selected ? "border-indigo-500 bg-indigo-50" : "border-gray-200 bg-white"
                    }`}
                  >
                    <span
                      className="w-4 h-4 rounded-full border"
                      style={{ backgroundColor: color.hexCode || "#ddd" }}
                    />
                    {displayAr(color.name)}
                  </button>
                );
              })}
              {colors.length === 0 && <span className="text-sm text-gray-500">أضف ألواناً من صفحة الألوان أولاً</span>}
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-4">التسعير والمخزون</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-gray-600">السعر الأساسي</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.basePrice}
                onChange={(e) => setForm({ ...form, basePrice: e.target.value })}
                className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl"
              />
            </div>
            <label className="flex items-center gap-2 mt-7 text-sm">
              <Switch
                checked={form.requiresVariants}
                onChange={(checked) => setForm({ ...form, requiresVariants: checked })}
                label="يتطلب متغيرات (لون / موديل)"
              />
            </label>
            {!form.requiresVariants && (
              <div>
                <label className="text-sm text-gray-600">المخزون</label>
                <input
                  type="number"
                  min="0"
                  value={form.stockQuantity}
                  onChange={(e) => setForm({ ...form, stockQuantity: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl"
                />
              </div>
            )}
          </div>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-4">التخصيص</h2>
          <Switch
            checked={form.allowsPersonalization}
            onChange={(checked) => setForm({ ...form, allowsPersonalization: checked })}
            label="السماح بالتخصيص"
          />
          {form.allowsPersonalization && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div>
                <label className="text-sm text-gray-600">تسمية التخصيص عربي</label>
                <input
                  value={form.personalizationLabelAr}
                  onChange={(e) => setForm({ ...form, personalizationLabelAr: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl"
                  placeholder="مثال: الاسم على الكفر"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">تسمية التخصيص إنجليزي</label>
                <input
                  value={form.personalizationLabelEn}
                  onChange={(e) => setForm({ ...form, personalizationLabelEn: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl"
                  dir="ltr"
                  placeholder="e.g. Name on case"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">أقصى طول (افتراضي 12)</label>
                <input
                  type="number"
                  min="1"
                  value={form.maxNameLength}
                  onChange={(e) => setForm({ ...form, maxNameLength: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl"
                />
              </div>
              <Switch
                checked={form.requiresArabicName}
                onChange={(checked) => setForm({ ...form, requiresArabicName: checked })}
                label="الاسم العربي مطلوب"
              />
              <Switch
                checked={form.requiresEnglishName}
                onChange={(checked) => setForm({ ...form, requiresEnglishName: checked })}
                label="الاسم الإنجليزي مطلوب"
              />
            </div>
          )}
        </section>

        <section>
          <h2 className="text-xl font-bold mb-4">إعدادات العمل</h2>
          <div className="flex flex-wrap gap-6">
            <Switch
              checked={form.requiresDeposit}
              onChange={(checked) => setForm({ ...form, requiresDeposit: checked })}
              label="يتطلب عربون"
            />
            <Switch
              checked={form.isActive}
              onChange={(checked) => setForm({ ...form, isActive: checked })}
              label="نشط"
            />
          </div>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-4">الصور</h2>
          <ImagePicker urls={form.imageUrls} onChange={(imageUrls) => setForm({ ...form, imageUrls })} />
        </section>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="submit"
            disabled={saving}
            className="w-full md:w-auto px-8 py-3 rounded-xl bg-indigo-600 text-white font-semibold flex items-center justify-center gap-2"
          >
            <Save size={16} /> {saving ? "جاري الحفظ..." : "حفظ المنتج"}
          </button>
          {isEdit && (
            <button
              type="button"
              onClick={() => setDeleteProductOpen(true)}
              className="w-full md:w-auto px-8 py-3 rounded-xl bg-red-50 text-red-700 font-semibold flex items-center justify-center gap-2"
            >
              <Trash2 size={16} /> حذف المنتج
            </button>
          )}
        </div>
      </form>

      {isEdit && (
        <section className="mt-8 bg-white/90 rounded-2xl shadow border border-white/30 p-5 md:p-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">المتغيرات</h2>
            {form.requiresVariants && (
              <button
                onClick={openVariantCreate}
                className="px-4 py-2 rounded-xl bg-indigo-600 text-white font-semibold flex items-center gap-2"
              >
                <Plus size={16} /> إضافة متغير
              </button>
            )}
          </div>

          {!form.requiresVariants ? (
            <p className="text-gray-500">هذا المنتج يُباع بدون متغيرات.</p>
          ) : loadingVariants ? (
            <TableSkeleton rows={3} />
          ) : variants.length === 0 ? (
            <EmptyState text="لا توجد متغيرات بعد" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-right min-w-[900px]">
                <thead className="bg-gray-50 text-sm text-gray-600">
                  <tr>
                    <th className="p-3">صورة</th>
                    <th className="p-3">اسم المتغير</th>
                    <th className="p-3">SKU</th>
                    <th className="p-3">السعر</th>
                    <th className="p-3">المخزون</th>
                    <th className="p-3">حد المخزون المنخفض</th>
                    <th className="p-3">الحالة</th>
                    <th className="p-3">إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {variants.map((variant) => {
                    const { urls, meta } = splitVariantImages(variant.imageUrls);
                    const variantName =
                      meta.nameAr ||
                      meta.nameEn ||
                      `${displayAr(variant.colorName)}${variant.typeName ? ` / ${variant.typeName}` : ""}`;
                    return (
                      <tr key={variant.productVariantId} className="border-t border-gray-100">
                        <td className="p-3">
                          {urls[0] ? (
                            <img src={resolveMediaUrl(urls[0])} alt="" className="w-12 h-12 rounded-lg object-cover" />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-gray-100" />
                          )}
                        </td>
                        <td className="p-3 font-medium">{variantName}</td>
                        <td className="p-3 font-mono text-sm">{variant.code}</td>
                        <td className="p-3">{variant.price} EGP</td>
                        <td className="p-3">
                          <StockBadge quantity={variant.stockQuantity} threshold={meta.lowStockThreshold} />
                        </td>
                        <td className="p-3">{meta.lowStockThreshold || DEFAULT_LOW_STOCK}</td>
                        <td className="p-3">
                          <span
                            className={`px-2 py-1 rounded-full text-sm ${
                              variant.isAvailable ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"
                            }`}
                          >
                            {variant.isAvailable ? "نشط" : "معطّل"}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => openVariantEdit(variant)}
                              className="p-2 rounded-lg bg-indigo-50 text-indigo-700"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => setDeleteVariant(variant)}
                              className="p-2 rounded-lg bg-red-50 text-red-700"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {variantForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 my-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">{editingVariant ? "تعديل المتغير" : "متغير جديد"}</h3>
              <button onClick={() => setVariantForm(null)}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={saveVariant} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-600">اسم المتغير عربي</label>
                  <input
                    value={variantForm.nameAr}
                    onChange={(e) => setVariantForm({ ...variantForm, nameAr: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600">اسم المتغير إنجليزي</label>
                  <input
                    value={variantForm.nameEn}
                    onChange={(e) => setVariantForm({ ...variantForm, nameEn: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl"
                    dir="ltr"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600">اللون</label>
                  <select
                    value={variantForm.colorId}
                    onChange={(e) => setVariantForm({ ...variantForm, colorId: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl bg-white"
                    required
                  >
                    <option value="">اختر اللون</option>
                    {colors.map((color) => (
                      <option key={color.colorId} value={color.colorId}>
                        {displayAr(color.name)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-600">موديل الهاتف (اختياري)</label>
                  <select
                    value={variantForm.typeId}
                    onChange={(e) => setVariantForm({ ...variantForm, typeId: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl bg-white"
                  >
                    <option value="">بدون</option>
                    {types.map((type) => (
                      <option key={type.typeId} value={type.typeId}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-600">SKU</label>
                  <input
                    value={variantForm.code}
                    onChange={(e) => setVariantForm({ ...variantForm, code: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600">السعر</label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={variantForm.price}
                    onChange={(e) => setVariantForm({ ...variantForm, price: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600">المخزون</label>
                  <input
                    type="number"
                    min="0"
                    value={variantForm.stockQuantity}
                    onChange={(e) => setVariantForm({ ...variantForm, stockQuantity: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600">حد المخزون المنخفض</label>
                  <input
                    type="number"
                    min="0"
                    value={variantForm.lowStockThreshold}
                    onChange={(e) => setVariantForm({ ...variantForm, lowStockThreshold: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl"
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Switch
                  checked={variantForm.isAvailable}
                  onChange={(checked) => setVariantForm({ ...variantForm, isAvailable: checked })}
                  label="نشط"
                />
              </label>
              <ImagePicker
                urls={variantForm.imageUrls}
                onChange={(imageUrls) => setVariantForm({ ...variantForm, imageUrls })}
              />
              <button
                type="submit"
                disabled={savingVariant}
                className="w-full py-3 rounded-xl bg-indigo-600 text-white font-semibold"
              >
                {savingVariant ? "جاري الحفظ..." : "حفظ المتغير"}
              </button>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteVariant}
        title="حذف المتغير"
        message="هل تريد حذف هذا المتغير؟ لا يمكن الحذف إذا كان مستخدماً في طلبات."
        onCancel={() => setDeleteVariant(null)}
        onConfirm={confirmDeleteVariant}
        loading={deletingVariant}
      />
      <ConfirmDialog
        open={deleteProductOpen}
        title="حذف المنتج"
        message="هل تريد حذف هذا المنتج وكل متغيراته؟ لا يمكن الحذف إذا كان مستخدماً في طلبات."
        onCancel={() => setDeleteProductOpen(false)}
        onConfirm={confirmDeleteProduct}
        loading={deletingProduct}
      />
    </CatalogShell>
  );
};

export default ProductFormPage;
